import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { HELP_ROUTE } from '../constants';

/**
 * What the registration screens share: a heading and lede on every one of them, and for the
 * steps also the quiet way back and the one piece of state a step owns — whether its request
 * is in flight and what refused it.
 *
 * A step holds no part of the run. What it owns is what a respondent typed and why the server
 * said no to it, which is also the whole of what survives a refusal: the run itself is
 * re-rendered from the view the page holds, and only from that.
 */

/** A screen's heading, and its lede where it has one — the Add page's chooser has none. */
export function StepHeading({ title, lede }: { title: string; lede?: React.ReactNode }) {
  return (
    <>
      <h1 className={lede ? undefined : 'mb-8'}>{title}</h1>
      {lede && (
        <p className="mt-3 mb-8 text-[0.9375rem] leading-relaxed text-muted-foreground">{lede}</p>
      )}
    </>
  );
}

/** Help, by the one name the registration screens give it until Veritant publishes an address
 *  (Q3) — a link, never a button: the screens around it are counted by their buttons. */
export function HelpLink() {
  return (
    <Link
      to={HELP_ROUTE}
      className="font-medium underline-offset-4 hover:text-[var(--color-royal-blue)] hover:underline"
    >
      How to reach Veritant
    </Link>
  );
}

/**
 * The step back, under the primary action and deliberately quiet — never what the screen is
 * for. A button rather than a link because each one is a request (`/abandonment`,
 * `/revision`), not a navigation. The arrow is decoration, so the name is the words alone.
 */
export function QuietAction({
  label,
  disabled,
  onClick
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'mt-5 justify-self-center text-sm text-muted-foreground',
        'hover:text-[var(--color-royal-blue)] disabled:opacity-60'
      )}
    >
      <span aria-hidden="true">← </span>
      {label}
    </button>
  );
}

/**
 * A field's server messages, in the order sent. One goes in the field's own error slot
 * (`slotMessage`); several go in a list under the control instead (`MessageList`), because
 * the kit draws its slot *after* the field's children — a first message there and the rest
 * above it would read upside down.
 */
export function slotMessage(messages: string[] | undefined): string | undefined {
  return messages?.length === 1 ? messages[0] : undefined;
}

export function MessageList({ messages }: { messages: string[] | undefined }) {
  if (!messages || messages.length < 2) return null;
  return (
    <ul className="mt-1 list-inside list-disc text-xs text-destructive">
      {messages.map((message) => (
        <li key={message}>{message}</li>
      ))}
    </ul>
  );
}

/**
 * A step's request, one at a time: `pending` while it is out, and `error` for whatever the
 * step has to render itself once it comes back refused. What the run as a whole answers —
 * the run finished, closed, moved on or unknown — never arrives here; the page has already
 * replaced the step by then.
 */
export function useStepAction() {
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  const run = React.useCallback(async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (refusal) {
      setError(refusal);
    } finally {
      setPending(false);
    }
  }, []);

  return { pending, error, run };
}
